import { TrophyLineIcon } from 'assets/icons';
import useText from 'hooks/useText';
import { goalLabel as shownGoal } from './statsGoals';
import { Delta } from './ResultTiles';
import StatsCard, { CardAside } from './StatsCard';
import { countDelta, percent, pointsDelta, rate } from './statsFormat';

const ROWS = ['calls', 'pickedUp', 'discussions', 'rdvTaken'];
const RATES = [
  ['ratePickedUp', (total) => rate(total.pickedUp, total.calls)],
  ['rateDiscussion', (total) => rate(total.discussions, total.pickedUp)],
  ['rateRdvPerDiscussion', (total) => rate(total.rdvTaken, total.discussions)],
  ['rateRdvPerCall', (total) => rate(total.rdvTaken, total.calls)],
];

// A row of the funnel: the volume in grey, the goal's figure (RDV pris) in
// orange.
const FunnelRow = ({ label, value, max, accent, delta, goal, goalLabel }) => (
  <div className="flex items-center gap-4">
    <p className="w-[110px] shrink-0 text-sm leading-5 text-gray-600 tab:w-[170px]">
      {label}
    </p>
    <div className="h-2.5 min-w-0 flex-1 overflow-hidden rounded-[5px] bg-gray-100">
      <div
        className={`h-full rounded-[5px] transition-[width] duration-500 ${accent ? 'bg-yellow-500' : 'bg-gray-400'}`}
        style={{ width: `${(value / max) * 100}%` }}
      />
    </div>
    <p className="w-8 shrink-0 text-right text-sm font-semibold leading-5 text-gray-900">
      {value}
    </p>
    {/* The goal the agency manager set (the trophies of the sheet). */}
    <span
      className="flex w-14 shrink-0 items-center justify-end gap-1 text-xs text-gray-400"
      aria-label={goal != null ? goalLabel : undefined}
    >
      {goal != null && (
        <>
          <TrophyLineIcon wh={12} color="currentColor" />
          {shownGoal(goal)}
        </>
      )}
    </span>
    <span className="w-8 shrink-0 text-right">
      <Delta delta={delta} />
    </span>
  </div>
);

// A figure on a white tile with a grey border, like the cards.
const RateTile = ({ value, delta, label }) => (
  <div className="flex min-w-0 flex-col gap-0.5 rounded-[14px] border border-gray-100 bg-white px-4 py-3.5">
    <p className="flex items-baseline gap-1.5">
      <span className="text-lg font-semibold leading-6 tracking-[-0.3px] text-gray-900">
        {value}
      </span>
      <Delta delta={delta} />
    </p>
    <p className="text-xs leading-4 text-gray-600">{label}</p>
  </div>
);

/**
 * « Prospection » of a period: the funnel from the calls to the appointments
 * booked, then its rates, each against the period before (`previous`).
 */
const ProspectionCard = ({ current, previous, aside, goals = {} }) => {
  const { text } = useText('stats');
  const max = Math.max(1, ...ROWS.map((key) => current[key]));
  return (
    <StatsCard
      title={text('prospection')}
      aside={<CardAside>{aside}</CardAside>}
    >
      {ROWS.map((key) => (
        <FunnelRow
          key={key}
          label={text(`funnel_${key}`)}
          value={current[key]}
          max={max}
          accent={key === 'rdvTaken'}
          delta={countDelta(current[key], previous[key])}
          goal={goals[key]}
          goalLabel={
            goals[key] != null
              ? text('goalLabel', { value: shownGoal(goals[key]) })
              : undefined
          }
        />
      ))}
      <div className="grid grid-cols-2 gap-3 tab:grid-cols-4">
        {RATES.map(([key, of]) => (
          <RateTile
            key={key}
            value={percent(of(current))}
            delta={pointsDelta(of(current), of(previous))}
            label={text(key)}
          />
        ))}
      </div>
    </StatsCard>
  );
};

export default ProspectionCard;
